import { getValidSecondaryEnv, isNamedEnv, type NamedEnv } from './types'

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

// ─── 공개 타입 ────────────────────────────────────────────────────
export type DevToolbarOptions = {
  onApiEnvApply: (url: string) => void
  onSecondaryEnvApply: (secondaryEnv: NamedEnv, customUrl: string) => void
  popupProps?: { off: boolean; togglePopup: () => void }
}

export type DevToolbarHandle = {
  /** 팝업 ON/OFF 상태 외부 변경 시 동기화 (MobX observer 등에서 호출) */
  setPopupOff: (off: boolean) => void
  unmount: () => void
}

// ─── 디자인 토큰 ──────────────────────────────────────────────────
const C = {
  bg: '#0f1115',
  border: 'rgba(255,255,255,0.08)',
  borderStrong: 'rgba(255,255,255,0.14)',
  fg: '#e7e9ee',
  fgDim: '#8b909c',
  fgMute: '#5a5f6b',
  dev: '#22c55e',
  stg: '#f59e0b',
  live: '#ef4444',
  custom: '#a78bfa',
  shadow: '0 10px 30px rgba(0,0,0,0.45), 0 2px 6px rgba(0,0,0,0.3)',
  mono: "ui-monospace,'JetBrains Mono','SF Mono',Menlo,monospace",
  font: "ui-sans-serif,-apple-system,BlinkMacSystemFont,'Pretendard',system-ui,sans-serif",
}
// 메인 API(maxios)만 가리키는 dev 계열 변형 서버.
// named env(dev/stg/live)와 달리 보조 서버는 '서비스 서버 외' 라디오를 따른다 (= custom 과 동일 동작).
const PRESET_SERVERS = [
  { value: 'lead', label: 'lead', url: 'https://api-dev-lead.mathflat.com', color: '#2dd4bf' },
  { value: 'prime', label: 'prime', url: 'https://api-dev-prime.mathflat.com', color: '#3b82f6' },
  {
    value: 'next-2',
    label: 'next-2',
    url: 'https://api-dev-next-2.mathflat.com',
    color: '#ec4899',
  },
]

const ENV_COLOR: Record<string, string> = {
  dev: C.dev,
  stg: C.stg,
  live: C.live,
  custom: C.custom,
  ...Object.fromEntries(PRESET_SERVERS.map((p) => [p.value, p.color])),
}
const envColor = (env: string) => ENV_COLOR[env] ?? C.fgMute

const SERVER_OPTIONS = [
  { value: 'dev', label: 'dev' },
  { value: 'stg', label: 'stg' },
  { value: 'live', label: 'live' },
]

// ─── CSS 주입 ─────────────────────────────────────────────────────
function injectStyles(id: string): () => void {
  if (document.getElementById(id)) return () => {}
  const el = document.createElement('style')
  el.id = id
  el.textContent = `
    @keyframes devtb-pulse {
      0%,100% { box-shadow: 0 0 0 0 rgba(239,68,68,.55), 0 0 8px rgba(239,68,68,.8); }
      50%      { box-shadow: 0 0 0 6px rgba(239,68,68,0), 0 0 14px rgba(239,68,68,.9); }
    }
    @keyframes devtb-live-glow {
      0%,100% { box-shadow: ${C.shadow}, 0 0 0 4px rgba(239,68,68,.1); }
      50%     { box-shadow: ${C.shadow}, 0 0 0 6px rgba(239,68,68,.22); }
    }
    .devtb-bar {
      display: flex; align-items: center;
      background: ${C.bg}; border-radius: 12px; color: ${C.fg};
      font-family: ${C.font}; padding-block: 10px;
      cursor: grab; user-select: none;
      border: 1px solid ${C.borderStrong}; box-shadow: ${C.shadow};
    }
    .devtb-bar.live {
      border-color: rgba(239,68,68,.45) !important;
      animation: devtb-live-glow 2s ease-in-out infinite;
    }
    .devtb-section {
      display: flex; flex-direction: column; gap: 4px;
      padding: 0 12px; border-right: 1px solid ${C.border};
    }
    .devtb-label {
      font-size: 9.5px; font-weight: 700; letter-spacing: 1.1px;
      text-transform: uppercase; color: ${C.fgDim}; font-family: ${C.mono};
    }
    .devtb-row { display: flex; align-items: center; gap: 6px; }
    .devtb-seg {
      display: inline-flex; padding: 2px;
      background: rgba(0,0,0,.35); border: 1px solid ${C.border};
      border-radius: 8px; gap: 1px;
    }
    .devtb-seg-btn {
      all: unset; cursor: pointer;
      display: inline-flex; align-items: center; gap: 5px;
      padding: 5px 10px; border-radius: 6px;
      font-size: 11.5px; font-weight: 600; font-family: ${C.mono};
      color: ${C.fgDim}; text-transform: uppercase; transition: all 120ms ease;
    }
    .devtb-seg-btn.active {
      color: ${C.fg};
      background: rgba(255,255,255,.08);
      box-shadow: inset 0 0 0 1px rgba(255,255,255,.06);
    }
    .devtb-seg-btn.active.danger {
      background: rgba(239,68,68,.18);
      box-shadow: inset 0 0 0 1px rgba(239,68,68,.45);
    }
    .devtb-dot {
      width: 6px; height: 6px; border-radius: 999px; flex: none;
    }
    .devtb-popup-btn {
      all: unset; cursor: pointer; padding: 4px 8px; border-radius: 6px;
      font-size: 11px; font-weight: 600; font-family: ${C.mono};
      color: ${C.fgMute}; border: 1px solid ${C.border}; transition: all 120ms ease;
    }
    .devtb-popup-btn.on {
      color: ${C.stg};
      border-color: rgba(245,158,11,.5);
      background: rgba(245,158,11,.08);
    }
    .devtb-icon-btn {
      all: unset; cursor: pointer;
      width: 22px; height: 22px;
      display: inline-flex; align-items: center; justify-content: center;
      border-radius: 6px; color: ${C.fgDim};
    }
    .devtb-icon-area { display: flex; align-items: center; padding: 0 8px; }
    .devtb-pill {
      all: unset; cursor: grab;
      display: inline-flex; align-items: center; gap: 8px;
      padding: 7px 12px; background: ${C.bg}; border-radius: 999px;
      color: ${C.fg}; font-family: ${C.font};
      font-size: 12px; font-weight: 600; user-select: none;
      border: 1px solid ${C.border}; box-shadow: ${C.shadow};
    }
    .devtb-pill.live {
      border-color: rgba(239,68,68,.55) !important;
      animation: devtb-live-glow 2s ease-in-out infinite;
    }
    .devtb-pill-dot { width: 7px; height: 7px; border-radius: 999px; }
    .devtb-pill-dot.live { animation: devtb-pulse 1.6s ease-in-out infinite; }
    .devtb-pill-label { font-family: ${C.mono}; font-size: 11px; text-transform: uppercase; }
  `
  document.head.appendChild(el)
  return () => el.remove()
}

// ─── HTML 빌더 ────────────────────────────────────────────────────
function buildDot(color: string, extraStyle = ''): string {
  return `<span class="devtb-dot" style="background:${color};box-shadow:0 0 5px ${color}80;${extraStyle}"></span>`
}

function buildSegmented(
  options: { value: string; label: string }[],
  active: string,
  danger = false,
): string {
  return `<div class="devtb-seg">${options
    .map((opt) => {
      const isActive = opt.value === active
      const isDanger = danger && opt.value === 'live'
      return `<button class="devtb-seg-btn${isActive ? ' active' : ''}${isActive && isDanger ? ' danger' : ''}" data-value="${opt.value}">
        ${buildDot(envColor(opt.value))}${opt.label}
      </button>`
    })
    .join('')}</div>`
}

function buildSection(label: string, hint: string, content: string): string {
  const hintHtml = hint
    ? `<span style="opacity:.6;font-weight:400">· ${hint}</span>`
    : ''
  return `<div class="devtb-section">
    <div class="devtb-label">${label}${hintHtml}</div>
    <div class="devtb-row">${content}</div>
  </div>`
}

const chevronDown = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="6 9 12 15 18 9"/></svg>`
const chevronUp   = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="${C.fgDim}" stroke-width="2.5" stroke-linecap="round"><polyline points="6 15 12 9 18 15"/></svg>`

// ─── 마운트 ───────────────────────────────────────────────────────
export function mountDevToolbar(options: DevToolbarOptions): DevToolbarHandle {
  const removeStyles = injectStyles('devtb-styles')

  // 내부 상태
  const rawApi = localStorage.getItem('DEV_API') ?? 'dev'
  const isCustomInit = !isNamedEnv(rawApi)
  let open = true
  let apiEnv = isCustomInit ? 'custom' : rawApi
  let serverEnv: NamedEnv = getValidSecondaryEnv(localStorage.getItem('DEV_API_SECONDARY_ENV'))
  let customUrl = isCustomInit ? rawApi : ''
  let popupOff = options.popupProps?.off ?? true
  let posX = 20
  let posY = window.innerHeight - 120

  const root = document.createElement('div')
  root.style.cssText = 'position:fixed;z-index:2147483000'
  document.body.appendChild(root)

  // 드래그
  let drag: { sx: number; sy: number; px: number; py: number } | null = null
  const onMouseMove = (e: MouseEvent) => {
    if (!drag) return
    posX = Math.max(0, drag.px + (e.clientX - drag.sx))
    posY = Math.max(0, drag.py + (e.clientY - drag.sy))
    root.style.left = posX + 'px'
    root.style.top  = posY + 'px'
  }
  const onMouseUp = () => {
    drag = null
    window.removeEventListener('mousemove', onMouseMove)
    window.removeEventListener('mouseup', onMouseUp)
  }
  const startDrag = (e: MouseEvent) => {
    e.preventDefault()
    drag = { sx: e.clientX, sy: e.clientY, px: posX, py: posY }
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
  }

  // 렌더
  function render() {
    root.style.left = posX + 'px'
    root.style.top  = posY + 'px'
    root.innerHTML  = open ? buildBar() : buildPill()
    attachListeners()
  }

  const isLive = () =>
    apiEnv === 'live' || (apiEnv === 'custom' && serverEnv === 'live')

  // apiEnv==='custom' 이면서 customUrl 이 프리셋과 일치하면 그 프리셋이 활성 (free-form custom 과 구분)
  const matchedPreset = () =>
    apiEnv === 'custom' ? PRESET_SERVERS.find((p) => p.url === customUrl) : undefined
  const activeValue = () => matchedPreset()?.value ?? apiEnv

  function buildApiLabel(): string {
    if (apiEnv !== 'custom' || !customUrl || matchedPreset()) return 'custom'
    return `custom | ${escapeHtml(customUrl.replace(/^https?:\/\//, '').replace(/\/$/, ''))}`
  }

  function buildBar(): string {
    const envOptions = [
      { value: 'dev',    label: 'dev' },
      { value: 'stg',    label: 'stg' },
      { value: 'live',   label: 'live' },
      ...PRESET_SERVERS.map((p) => ({ value: p.value, label: p.label })),
      { value: 'custom', label: buildApiLabel() },
    ]
    const live = isLive()

    let html = `<div class="devtb-bar${live ? ' live' : ''}">`
    html += buildSection('서비스 서버', '', buildSegmented(envOptions, activeValue(), true))
    if (apiEnv === 'custom') {
      html += buildSection('서비스 서버 외', 'custom', buildSegmented(SERVER_OPTIONS, serverEnv))
    }
    if (options.popupProps) {
      html += buildSection(
        'Popup',
        '',
        `<button class="devtb-popup-btn${popupOff ? '' : ' on'}">${popupOff ? 'Popup OFF' : 'Popup ON'}</button>`,
      )
    }
    html += `<div class="devtb-icon-area"><button class="devtb-icon-btn" data-action="collapse">${chevronDown}</button></div>`
    html += `</div>`
    return html
  }

  function buildPill(): string {
    const live = isLive()
    const val = activeValue()
    return `<button class="devtb-pill${live ? ' live' : ''}">
      <span class="devtb-pill-dot${live ? ' live' : ''}" style="background:${envColor(val)}"></span>
      <span class="devtb-pill-label">${val}</span>
      ${chevronUp}
    </button>`
  }

  function attachListeners() {
    const bar = root.querySelector<HTMLElement>('.devtb-bar')
    const pill = root.querySelector<HTMLElement>('.devtb-pill')

    if (bar) {
      bar.addEventListener('mousedown', (e) => {
        if ((e.target as HTMLElement).closest('button')) return
        startDrag(e)
      })

      const sections = bar.querySelectorAll('.devtb-section')
      const stopProp = (e: MouseEvent) => e.stopPropagation()

      // API 섹션 (첫 번째)
      sections[0]?.querySelectorAll<HTMLElement>('[data-value]').forEach((btn) => {
        btn.addEventListener('mousedown', stopProp)
        btn.addEventListener('click', () => handleApiEnvChange(btn.dataset['value']!))
      })
      // 보조 서버 섹션 (두 번째, custom일 때만 존재)
      if (apiEnv === 'custom') {
        sections[1]?.querySelectorAll<HTMLElement>('[data-value]').forEach((btn) => {
          btn.addEventListener('mousedown', stopProp)
          btn.addEventListener('click', () => handleServerEnvChange(btn.dataset['value']!))
        })
      }

      // 팝업 버튼
      const popupBtn = bar.querySelector<HTMLElement>('.devtb-popup-btn')
      if (popupBtn) {
        popupBtn.addEventListener('mousedown', stopProp)
        popupBtn.addEventListener('click', () => {
          options.popupProps!.togglePopup()
          popupOff = !popupOff
          render()
        })
      }

      // 접기 버튼
      const collapseBtn = bar.querySelector<HTMLElement>('[data-action="collapse"]')
      if (collapseBtn) {
        collapseBtn.addEventListener('mousedown', stopProp)
        collapseBtn.addEventListener('click', () => {
          open = false
          render()
        })
      }
    }

    if (pill) {
      pill.addEventListener('mousedown', startDrag)
      pill.addEventListener('click', () => {
        open = true
        render()
      })
    }
  }

  function handleApiEnvChange(next: string) {
    const preset = PRESET_SERVERS.find((p) => p.value === next)
    if (preset) {
      apiEnv = 'custom'
      customUrl = preset.url
      render()
      options.onApiEnvApply(preset.url)
      return
    }
    if (next !== 'custom') {
      apiEnv = next
      render()
      options.onApiEnvApply(next)
      return
    }
    const input = prompt(
      'http로 시작하는 커스텀 API URL을 입력해주세요.\n(예: https://api-dev.mathflat.com)\n* CORS 대응은 되어있지 않습니다.',
      customUrl || 'https://',
    )
    if (!input) return
    let parsed: URL
    try { parsed = new URL(input.trim()) } catch { return }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return
    apiEnv = 'custom'
    customUrl = parsed.toString()
    render()
    options.onApiEnvApply(customUrl)
  }

  function handleServerEnvChange(env: string) {
    const next = getValidSecondaryEnv(env)
    if (next === serverEnv) return
    localStorage.setItem('DEV_API_SECONDARY_ENV', next)
    serverEnv = next
    render()
    options.onSecondaryEnvApply(next, customUrl)
  }

  render()

  return {
    setPopupOff: (off: boolean) => {
      popupOff = off
      if (open) render()
    },
    unmount: () => {
      root.remove()
      removeStyles()
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    },
  }
}
